import { useTranslation } from "react-i18next";
import { ArrowRotateLeft, SearchNormal1 } from "iconsax-react";
import cn from "classnames";
import { Input } from "@/shared/ui";
import { TEMPLATE_CATEGORIES } from "../consts";
import type { TemplateCategory } from "../consts";

interface Props {
  searchQuery: string;
  onSearchChange: (value: string) => void;
  selectedCategories: Set<TemplateCategory>;
  onCategoryToggle: (category: TemplateCategory) => void;
  onReset: () => void;
  hasActiveFilters: boolean;
}

function FilterSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-2">
      <span className="text-label-sm content-base-primary">{title}</span>
      {children}
    </div>
  );
}

export default function TemplatesFilterPanel({
  searchQuery,
  onSearchChange,
  selectedCategories,
  onCategoryToggle,
  onReset,
  hasActiveFilters,
}: Props) {
  const { t } = useTranslation("LegalTemplatesPage");

  return (
    <aside className="w-2xs shrink-0 flex flex-col radius-lg surface-base-fill overflow-hidden self-start">
      <div className="border-b surface-container-stroke px-5 py-4 flex items-center justify-between">
        <p className="text-label-lg content-base-primary">{t("filterPanel.filtersTitle")}</p>
        {hasActiveFilters && (
          <button
            type="button"
            className="flex justify-end items-center gap-1 text-label-sm content-base-secondary cursor-pointer"
            onClick={onReset}>
            {t("filterPanel.resetAll")}
            <ArrowRotateLeft size={16} color="currentColor" />
          </button>
        )}
      </div>

      <div className="px-5 py-4">
        <Input
          placeholder={t("filterPanel.searchPlaceholder")}
          value={searchQuery}
          onChange={(e) => onSearchChange(e.target.value)}
          icon={<SearchNormal1 size={16} color="currentColor" />}
        />
      </div>

      <div className="border-t surface-container-stroke px-5 py-4">
        <FilterSection title={t("filterPanel.category")}>
          <div className="flex flex-wrap gap-2">
            {TEMPLATE_CATEGORIES.map((cat) => (
              <button
                key={cat}
                type="button"
                className={cn(
                  "px-3 py-1.5 radius-sm text-label-sm cursor-pointer transition-colors duration-150",
                  selectedCategories.has(cat)
                    ? "background-brand-fill content-static-white"
                    : "surface-component-fill content-base-primary hover:opacity-80",
                )}
                onClick={() => onCategoryToggle(cat)}>
                {t(`categories.${cat}`)}
              </button>
            ))}
          </div>
        </FilterSection>
      </div>
    </aside>
  );
}
